/* eslint-disable */
import { toLower } from 'lodash/fp';
import { PaymentCardType } from '@mews/enums';
import paymentCardImages from './images/credit-cards';
import {
    getPaymentCardTypeConfigFromNumber,
    luhnCheck,
    PaymentCardImageTypes,
    PaymentCardTypeConfig,
} from '@mews-framework/payments';

interface PaymentCardExpirationDate {
    month: number;
    year: number;
}

const isValidImageCardType = (card: string): card is keyof PaymentCardImageTypes =>
    Object.keys(paymentCardImages).some((key) => toLower(card) === toLower(key));

const paymentCard = {
    isValidImageCardType,

    cardFromNumber(num: string | number): PaymentCardTypeConfig | null {
        return getPaymentCardTypeConfigFromNumber(`${num}`) ?? null;
    },

    luhnCheck(num: string | number): boolean {
        return luhnCheck(`${num}`);
    },

    restrictNumeric(e: React.KeyboardEvent): void {
        let input = String.fromCharCode(e.which);
        if (e.which === 32 || !/[\d\s]/.test(input)) {
            e.preventDefault();
        }
    },

    hasTextSelected(node: HTMLInputElement): boolean {
        // if some text is selected
        if (node.selectionStart != null && node.selectionStart !== node.selectionEnd) {
            return true;
        }

        // if some text is selected in IE
        let ref;
        if (
            (typeof document !== 'undefined' && document !== null
                ? (ref = (document as $TSFixMe).selection) != null
                    ? ref.createRange
                    : void 0
                : void 0) != null
        ) {
            if ((document as $TSFixMe).selection.createRange().text) {
                return true;
            }
        }

        return false;
    },

    formatCardNumber(num: string): string | undefined {
        num = num.replace(/\D/g, '');
        let card = this.cardFromNumber(num);
        if (!card) {
            return num;
        }
        let upperLength = card.length[card.length.length - 1];
        num = num.slice(0, upperLength);
        let ref;
        if (card.format.global) {
            return (ref = num.match(card.format)) != null ? ref.join(' ') : void 0;
        } else {
            let groups = card.format.exec(num);
            if (groups == null) {
                return;
            }
            groups.shift();
            const filteredGroups = groups.filter((n) => n);

            return filteredGroups.join(' ');
        }
    },

    formatExpiry(expiry: string): string {
        let parts = expiry.match(/^\D*(\d{1,2})(\D+)?(\d{1,4})?/);
        if (!parts) {
            return '';
        }
        let mon = parts[1] || '';
        let sep = parts[2] || '';
        let year = parts[3] || '';
        if (year.length > 0) {
            sep = ' / ';
        } else if (sep === ' /') {
            mon = mon.substring(0, 1);
            sep = '';
        } else if (mon.length === 2 || sep.length > 0) {
            sep = ' / ';
        } else if (mon.length === 1 && mon !== '0' && mon !== '1') {
            mon = '0' + mon;
            sep = ' / ';
        }

        return mon + sep + year;
    },

    validateCardNumber(num: string | number): boolean {
        num = (num + '').replace(/\s+|-/g, '');
        if (!/^\d+$/.test(num)) {
            return false;
        }

        let card = this.cardFromNumber(num);
        if (!card) {
            return false;
        }

        return card.length.indexOf(num.length) >= 0 && (!card.luhn || this.luhnCheck(num));
    },

    validateCardExpiry(month: string, year: string): boolean {
        if (!(month && year)) {
            return false;
        }
        month = month.trim();
        year = year.trim();
        if (!/^\d+$/.test(month)) {
            return false;
        }
        if (!/^\d+$/.test(year)) {
            return false;
        }
        let monthNumber = parseInt(month);
        if (!(1 <= monthNumber && monthNumber <= 12)) {
            return false;
        }
        if (year.length === 2) {
            if (parseInt(year) < 70) {
                year = '20' + year;
            } else {
                year = '19' + year;
            }
        }
        if (year.length !== 4) {
            return false;
        }
        let expiry = new Date(parseInt(year), monthNumber);
        let currentTime = new Date();
        expiry.setMonth(expiry.getMonth() - 1);
        expiry.setMonth(expiry.getMonth() + 1, 1);

        return expiry > currentTime;
    },

    validateCardCVC(cvc: string): boolean {
        cvc = cvc.trim();
        if (!/^\d+$/.test(cvc)) {
            return false;
        }

        return cvc.length >= 3 && cvc.length <= 4;
    },

    cardExpiryValue(value: string): PaymentCardExpirationDate {
        value = value.replace(/\s/g, '');
        let [month, year] = value.split('/', 2);
        if ((year != null ? year.length : void 0) === 2 && /^\d+$/.test(year)) {
            let prefix = new Date().getFullYear();
            year = prefix.toString().slice(0, 2) + year;
        }

        return {
            month: parseInt(month, 10),
            year: parseInt(year, 10),
        };
    },

    getTypeFromPaymentCard(type: PaymentCardType): PaymentCardTypeConfig['type'] | null {
        const paymentCardTypeLowerCase = toLower(type);

        if (isValidImageCardType(paymentCardTypeLowerCase)) {
            return paymentCardTypeLowerCase;
        }

        return null;
    },
};

export default paymentCard;
